import type { OrderStatus, OrderUnifiedOutput } from "@/api/custom_types";
import { useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { useAtomValue } from "jotai";
import { useMemo } from "react";

import { useFacets } from "@/hooks/useFacetFilters";
import { cleanQuery, omit } from "@/utils/queryClient";
import { getTimeSinceText } from "@/utils/timestamp";
import {
	calculateTaxPercentage,
	formatNumber,
	getOrderEmail,
} from "@/utils/tools";

import { useTranslation } from "@/hooks/useTranslation";
import {
	ORDERS_FACET_SOURCE,
	useGetBuyerOrders,
	useGetOrders,
	useGetSellerOrders,
} from "@/services";
import { currentUserAtom } from "@/store/login";
import {
	type StatusTranslation,
	useOrderStatusTranslator,
} from "@/utils/translations";
import { Button, Chip, type ChipVariant } from "@st-marketplace/ui";

import type { Language } from "@/constants";
import RelationNameLink from "@/pages/relations/RelationNameLink";
import type { TranslationF } from "@/utils/type-utils";
import OrdersSingleAction from "../OrdersSingleAction";
import type { OrdersFilterValues, OrdersSchemaType } from "../types";

export type OrderRow = OrderUnifiedOutput;

const FACET_SCOPE_KEYS_BY_SCHEMA: Record<OrdersSchemaType, string[]> = {
	seller: ["seller"],
	buyer: ["buyer"],
	admin: [],
};

const STATUS_COLOR_TO_CHIP_VARIANT: Record<
	StatusTranslation["color"],
	ChipVariant
> = {
	success: "success",
	primary: "info",
	error: "error",
	warning: "warning",
};

function useOrdersListBySchema(args: {
	schemaType: OrdersSchemaType;
	query: Record<string, any>;
}) {
	const { schemaType, query } = args;

	const sellerRes = useGetSellerOrders(
		{ query },
		{ enabled: schemaType === "seller" },
	);
	const buyerRes = useGetBuyerOrders(
		{ query },
		{ enabled: schemaType === "buyer" },
	);
	const adminRes = useGetOrders({ query }, { enabled: schemaType === "admin" });

	if (schemaType === "seller") return sellerRes;
	if (schemaType === "buyer") return buyerRes;
	if (schemaType === "admin") return adminRes;
}

function buildColumns(args: {
	schemaType: OrdersSchemaType;
	t: TranslationF;
	lang: Language;
	navigateTo: (to: string) => void;
	translateStatus: (status: OrderStatus) => StatusTranslation;
}): ColumnDef<OrderRow>[] {
	const { schemaType, t, lang, navigateTo, translateStatus } = args;

	const cols: ColumnDef<OrderRow>[] = [
		{
			id: "ea_email",
			header: t("order.tableOrderEmail"),
			enableSorting: false,
			cell: ({ row }) => (
				<Button
					type="button"
					variant="text"
					color="primary"
					size="sm"
					onClick={() => navigateTo(`/orders/${row.original.pk}`)}
				>
					{getOrderEmail(row.original)}
				</Button>
			),
		},
	];

	if (schemaType !== "buyer") {
		cols.push({
			id: "buyer_name",
			header: t("order.tableBuyer"),
			enableSorting: false,
			cell: ({ row }) => (
				<RelationNameLink
					name={row.original.buyer_name}
					relationId={row.original.relation_id}
					party="buyer"
				/>
			),
		});
	}

	if (schemaType !== "seller") {
		cols.push({
			id: "seller_name",
			header: t("order.tableSeller"),
			enableSorting: false,
			cell: ({ row }) => (
				<RelationNameLink
					name={row.original.seller_name}
					relationId={row.original.relation_id}
					party="seller"
				/>
			),
		});
	}

	cols.push({
		id: "amount",
		header: t("order.tableDeliveryAmount"),
		enableSorting: false,
		cell: ({ row }) =>
			`${formatNumber(row.original.delivered / 1000, "count")} / ${formatNumber(
				row.original.amount / 1000,
				"count",
			)}${t("signs.thousands")}`,
	});

	if (schemaType !== "buyer") {
		cols.push({
			id: "tax",
			header: t("order.tableTax"),
			enableSorting: false,
			cell: ({ row }) => {
				const taken = "taken" in row.original ? row.original.taken : 0;
				const { delivered } = row.original;
				const tax = calculateTaxPercentage(delivered, taken);
				return formatNumber(tax, "percent");
			},
		});
	}

	cols.push(
		{
			id: "unit_price",
			header: t("order.tableUnitPrice"),
			enableSorting: false,
			cell: ({ row }) => `${formatNumber(row.original.unit_price, "price")}`,
		},
		{
			id: "total_price",
			header: t("order.tableTotalPrice"),
			enableSorting: false,
			cell: ({ row }) => `${formatNumber(row.original.total_price, "price")}`,
		},
		{
			id: "status",
			header: t("order.tableStatus"),
			enableSorting: false,
			cell: ({ row }) => {
				const { text, color } = translateStatus(row.original.status);
				return (
					<Chip variant={STATUS_COLOR_TO_CHIP_VARIANT[color]} size="sm">
						{text}
					</Chip>
				);
			},
		},
		{
			id: "deliver_estimated_at",
			header: t("order.tableEstimatedDelivery"),
			enableSorting: false,
			cell: ({ row }) => {
				const deliver_estimated_at =
					"deliver_estimated_at" in row.original
						? row.original.deliver_estimated_at
						: null;
				return (
					(deliver_estimated_at &&
						getTimeSinceText(deliver_estimated_at, t, lang)) ||
					"-"
				);
			},
		},
		{
			id: "created_date",
			header: t("order.tableDate"),
			enableSorting: false,
			cell: ({ row }) =>
				(row.original.created_date &&
					getTimeSinceText(row.original.created_date, t, lang)) ||
				"-",
		},
		{
			id: "actions",
			header: "",
			enableSorting: false,
			cell: ({ row }) => <OrdersSingleAction selectedOrder={row.original} />,
		},
	);

	return cols;
}

export function useOrdersConfig(args: {
	schemaType: OrdersSchemaType;
	filters: OrdersFilterValues;
	page: number;
	pageSize: number;
	buyerId?: string;
	sellerId?: string;
}) {
	const { schemaType, filters, page, pageSize, buyerId, sellerId } = args;

	const { t, lang } = useTranslation();
	const translateStatus = useOrderStatusTranslator(t);
	const navigate = useNavigate();
	const user = useAtomValue(currentUserAtom);

	const listQuery = useMemo(() => {
		const base = {
			page,
			page_size: pageSize,
			statuses: filters.status?.length ? filters.status : undefined,
			email: filters.email || undefined,
		};

		if (schemaType === "seller") {
			return cleanQuery({
				...base,
				seller: user?.id,
				buyer: (buyerId ?? filters.buyer) || undefined,
				buyer_id: (buyerId ?? filters.buyer) || undefined,
			});
		}

		if (schemaType === "buyer") {
			return cleanQuery({
				...base,
				buyer: user?.id,
				seller: (sellerId ?? filters.seller) || undefined,
				seller_id: (sellerId ?? filters.seller) || undefined,
			});
		}

		return cleanQuery({
			...base,
			buyer_id: filters.buyer || undefined,
			seller_id: filters.seller || undefined,
		});
	}, [
		schemaType,
		filters.status,
		filters.email,
		filters.buyer,
		buyerId,
		sellerId,
		filters.seller,
		page,
		pageSize,
		user?.id,
	]);

	const facetsScope = useMemo(() => {
		if (schemaType === "seller") return { seller: user?.id };
		if (schemaType === "buyer") return { buyer: user?.id };
		return {};
	}, [schemaType, user?.id]);

	// The schema's own party scopes the whole page, so it is not a facet filter.
	const facetsFilters = useMemo(
		() =>
			omit(listQuery as Record<string, unknown>, [
				"page",
				"page_size",
				...FACET_SCOPE_KEYS_BY_SCHEMA[schemaType],
			]),
		[listQuery, schemaType],
	);

	const listRes = useOrdersListBySchema({ schemaType, query: listQuery });

	const facetsCounts = useFacets({
		source: ORDERS_FACET_SOURCE,
		scope: facetsScope,
		filters: facetsFilters,
	});

	const columns = useMemo(
		() =>
			buildColumns({
				schemaType,
				t,
				lang,
				navigateTo: (to) => navigate({ to }),
				translateStatus,
			}),
		[schemaType, t, lang, navigate, translateStatus],
	);

	return {
		columns,

		listQuery,

		orders: listRes!.data,
		isLoading: listRes!.isLoading,
		isFetching: listRes!.isFetching,
		error: listRes!.error,
		refetch: listRes!.refetch,

		facetsCounts,
	};
}
